import React, { useState } from 'react';
import { 
  FileText, Mail, ExternalLink, X, Loader2, Eye, Download,
  Link2, Calendar, Hash, Upload, Delete, AlertTriangle, ChevronDown, ChevronUp
} from 'lucide-react';
import { 
  PictureAsPdf, 
  InsertDriveFile, 
  Visibility,
  ContentCopy as CopyAll
} from '@mui/icons-material';
import { 
  Paper, 
  Typography, 
  Box, 
  Chip, 
  Link, 
  IconButton, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';
import { 
  fetchBusiness4FlyoEmailText,
  uploadBusiness4FlyoDocument, 
  resolveBusiness4FlyoBoardingPassTraveller,
  updateBusiness4FlyoTicket,
  copyBusiness4FlyoDocument,
  updateBusiness4FlyoTicketDocuments
} from '../../services/api';

/**
 * Source & Documents Tab Component for Business4Flyo
 * Combines B4FSourceTab and B4FDocumentsTab content
 * 
 * @param {Object} props - Component props
 * @param {Array} props.source - Array of source objects (from B4FSourceTab)
 * @param {Array} props.attachments - Array of attachment objects (Supplier documents)
 * @param {Array} props.customerDocuments - Array of customer document objects
 * @param {number} props.ticketId - Ticket ID from URL
 * @param {number} props.agentId - Agent ID from ticket data
 * @param {Function} props.onUpdate - Callback to refresh data after update
 * @returns {JSX.Element} B4FSourceDocumentsTab component
 */
const B4FSourceDocumentsTab = ({ 
  source, 
  attachments, 
  customerDocuments, 
  ticketId, 
  agentId, 
  onUpdate 
}) => {
  // Source tab state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailText, setEmailText] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [sourceExpanded, setSourceExpanded] = useState(false); // Source section collapsed by default

  // Documents tab state
  const [activeDocumentTab, setActiveDocumentTab] = useState('supplier');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [shareWithCustomer, setShareWithCustomer] = useState(false);
  const [ambiguousBoardingPassMatches, setAmbiguousBoardingPassMatches] = useState([]);
  const [boardingPassResolutionSelections, setBoardingPassResolutionSelections] = useState({});
  const [boardingPassResolveLoading, setBoardingPassResolveLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isCustomerDocDelete, setIsCustomerDocDelete] = useState(false);

  // File type options
  const fileTypeOptions = [
    { value: 'boardingPass', label: 'Boarding Pass' },
    { value: 'eTicket', label: 'ETicket' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'policy', label: 'Policy' },
    { value: 'other', label: 'Other' },
  ];

  // Document type color mapping
  const documentTypeColors = {
    'ETicket': { bg: '#e0f2fe', color: '#0369a1', border: '#7dd3fc' },
    'eTicket': { bg: '#e0f2fe', color: '#0369a1', border: '#7dd3fc' },
    'Invoice': { bg: '#fef3c7', color: '#b45309', border: '#fcd34d' },
    'BoardingPass': { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
    'boardingPass': { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
    'Policy': { bg: '#f3e8ff', color: '#7c3aed', border: '#c4b5fd' },
    'Other': { bg: '#f3f4f6', color: '#4b5563', border: '#d1d5db' },
    'other': { bg: '#f3f4f6', color: '#4b5563', border: '#d1d5db' },
  };

  const hasSource = source && source.length > 0;
  const hasAttachments = attachments && attachments.length > 0;
  const hasCustomerDocuments = customerDocuments && customerDocuments.length > 0;
  const isEmailSource = source && source.some(item => item.name?.toLowerCase() === 'email');

  // ============ SOURCE TAB FUNCTIONS ============

  // Get badge color for source type
  const getSourceBadgeColor = (sourceName) => {
    const name = sourceName?.toLowerCase() || '';
    if (name === 'pdf') return 'bg-blue-50 text-blue-700 border-blue-200';
    if (name === 'email') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  // Get source icon
  const getSourceIcon = (sourceName) => {
    const name = sourceName?.toLowerCase() || '';
    if (name === 'email') return <Mail className="w-5 h-5" />;
    return <FileText className="w-5 h-5" />;
  };

  // Handle fetch email text
  const handleFetchEmailText = async () => {
    if (!agentId || !ticketId) {
      alert('Missing required information');
      return;
    }

    try {
      setEmailLoading(true);
      const response = await fetchBusiness4FlyoEmailText(parseInt(ticketId), agentId);
      setEmailText(response.data?.emailText || response.emailText || 'No email text found');
      setShowEmailDialog(true);
    } catch (error) {
      alert(`Error fetching email text: ${error.message}`);
    } finally {
      setEmailLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Modal Component
  const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          {/* Header - Solid color */}
          <div className="px-6 py-4 bg-blue-600 flex items-center justify-between flex-shrink-0">
            <h2 className="text-lg font-bold text-white">{title}</h2>
            <button 
              onClick={onClose}
              className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {children}
          </div>
        </div>
      </div>
    );
  };

  // ============ DOCUMENTS TAB FUNCTIONS ============

  // Get file type label from value
  const getFileTypeLabel = (value) => {
    const option = fileTypeOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  // Get document type styles
  const getDocumentTypeStyles = (type) => {
    return documentTypeColors[type] || { bg: '#f3f4f6', color: '#4b5563', border: '#d1d5db' };
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveDocumentTab(newValue);
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const formatTravellerName = (firstName, lastName, fallback = 'UNNAMED PASSENGER') => {
    const name = [firstName, lastName].filter(Boolean).join(' ').trim();
    return name ? name.toUpperCase() : fallback;
  };

  const openBoardingPassResolutionDialog = (matches) => {
    const selections = matches.reduce((acc, match) => {
      acc[match.resolutionToken] = '';
      return acc;
    }, {});
    setAmbiguousBoardingPassMatches(matches);
    setBoardingPassResolutionSelections(selections);
  };

  const clearBoardingPassResolutionDialog = () => {
    setAmbiguousBoardingPassMatches([]);
    setBoardingPassResolutionSelections({});
  };

  const handleResolveBoardingPassMatches = async () => {
    const missingSelection = ambiguousBoardingPassMatches.some(
      (match) => !boardingPassResolutionSelections[match.resolutionToken]
    );
    if (missingSelection) {
      alert('Select a traveller for each boarding pass');
      return;
    }

    try {
      setBoardingPassResolveLoading(true);
      for (const match of ambiguousBoardingPassMatches) {
        await resolveBusiness4FlyoBoardingPassTraveller({
          resolutionToken: match.resolutionToken,
          travellerId: Number(boardingPassResolutionSelections[match.resolutionToken]),
          ticketId,
          agentId
        });
      }

      clearBoardingPassResolutionDialog();
      if (onUpdate) onUpdate();
      alert('Boarding pass assigned successfully');
    } catch (error) {
      alert(`Error assigning boarding pass: ${error.message}`);
    } finally {
      setBoardingPassResolveLoading(false);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile || !fileType || !agentId || !ticketId) {
      alert('Please select a file and file type, and ensure agentId and ticketId are available');
      return;
    }

    try {
      setUploadLoading(true);
      const formData = new FormData();
      formData.append('agentId', agentId.toString());
      formData.append('ticketId', ticketId.toString());
      formData.append('file', selectedFile);
      formData.append('fileName', fileType);
      formData.append('userType', shareWithCustomer ? 'customer' : 'supplier');

      const response = await uploadBusiness4FlyoDocument(formData);
      const ambiguousMatches = response?.data?.ambiguousBoardingPassMatches?.filter(
        (match) => match.candidates?.length > 1
      ) || [];

      if (ambiguousMatches.length > 0) {
        openBoardingPassResolutionDialog(ambiguousMatches);
      }

      setShowUploadDialog(false);
      setSelectedFile(null);
      setFileType('');
      setShareWithCustomer(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      alert(`Error uploading document: ${error.message}`);
    } finally {
      setUploadLoading(false);
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (index, isCustomer) => {
    const doc = isCustomer ? customerDocuments[index] : attachments[index];
    setDocumentToDelete({ index, doc });
    setIsCustomerDocDelete(isCustomer);
    setShowDeleteDialog(true);
  };

  // Close delete dialog
  const closeDeleteDialog = () => {
    setShowDeleteDialog(false);
    setDocumentToDelete(null);
    setIsCustomerDocDelete(false);
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!documentToDelete || !agentId || !ticketId) {
      alert('Missing required information');
      return;
    }

    try {
      setDeleteLoading(true);
      const index = documentToDelete.index;
      
      if (isCustomerDocDelete) {
        const updatedDocuments = customerDocuments.map((doc, i) => (
          i === index
            ? {
                ...doc,
                isSharedWithCustomer: false,
                isShareWithCustomer: false
              }
            : doc
        ));
        await updateBusiness4FlyoTicketDocuments({
          agentId,
          ticketId: parseInt(ticketId),
          userType: 'customer',
          attachments: updatedDocuments
        });
      } else {
        const updatedAttachments = attachments.filter((_, i) => i !== index);
        await updateBusiness4FlyoTicket({
          agentId,
          ticketId: parseInt(ticketId),
          attachments: updatedAttachments
        });
      }
      
      closeDeleteDialog();
      if (onUpdate) onUpdate();
    } catch (error) {
      alert(`Error deleting document: ${error.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle copy document between tabs
  const handleCopyDocument = async (doc, fromUserType) => {
    if (!agentId || !ticketId) {
      alert('Missing required information');
      return;
    }

    const targetUserType = fromUserType === 'supplier' ? 'customer' : 'supplier';
    const attachmentPayload = fromUserType === 'supplier'
      ? {
          ...doc,
          isShareWithCustomer: true,
          isSharedWithCustomer: true
        }
      : doc;
    
    try {
      setCopyLoading(true);
      
      await copyBusiness4FlyoDocument({
        agentId,
        ticketId: parseInt(ticketId),
        userType: targetUserType,
        attachment: attachmentPayload
      });
      
      if (onUpdate) onUpdate();
    } catch (error) {
      alert(`Error copying document: ${error.message}`);
    } finally {
      setCopyLoading(false);
    }
  };

  // Get file icon based on file type
  const getFileIcon = (url, name) => {
    if (!url && !name) return <InsertDriveFile sx={{ fontSize: 40, color: '#9ca3af' }} />;
    
    const fileName = (name || url || '').toLowerCase();
    if (fileName.endsWith('.pdf')) {
      return <PictureAsPdf sx={{ fontSize: 40, color: '#ef4444' }} />;
    } else if (fileName.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/)) {
      return null;
    } else {
      return <InsertDriveFile sx={{ fontSize: 40, color: '#6b7280' }} />;
    }
  };

  // Check if document is an image
  const isImageFile = (url, name) => {
    const fileName = (name || url || '').toLowerCase();
    return fileName.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/);
  };

  // Render document list
  const renderDocumentList = (documents, isCustomerDocs) => {
    if (!documents || documents.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          No {isCustomerDocs ? 'customer' : 'all'} documents found
        </Typography>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {documents.map((doc, index) => {
          const isImage = isImageFile(doc.url, doc.name);
          const fileIcon = getFileIcon(doc.url, doc.name);
          const isSharedWithCustomer = !isCustomerDocs && (
            doc.isSharedWithCustomer === true ||
            doc.isShareWithCustomer === true ||
            doc.isSharedWithCustomer === 'true' ||
            doc.isShareWithCustomer === 'true'
          );
          
          return (
            <Box 
              key={index}
              sx={{ 
                p: 2, 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px',
                '&:hover': {
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }
              }}
            >
              {/* Preview Thumbnail */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '6px',
                  overflow: 'hidden',
                  flexShrink: 0,
                  bgcolor: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #e5e7eb'
                }}
              >
                {isImage && doc.url ? (
                  <img 
                    src={doc.url} 
                    alt={doc.name || 'Document preview'}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover' 
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <Box sx={{ display: isImage ? 'none' : 'flex' }}>
                  {fileIcon}
                </Box>
              </Box>

              {/* Document Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: '600' }}>
                    {doc.name 
                      ? (doc.name.length > 30 
                          ? `${doc.name.substring(0, 30)}...` 
                          : doc.name)
                      : 'Unnamed Document'}
                  </Typography>
                  {doc.fileType && (
                    <Chip 
                      label={getFileTypeLabel(doc.fileType)} 
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                  {doc.documentType && (
                    <Chip 
                      label={doc.documentType}
                      size="small"
                      sx={{
                        backgroundColor: getDocumentTypeStyles(doc.documentType).bg,
                        color: getDocumentTypeStyles(doc.documentType).color,
                        border: `1px solid ${getDocumentTypeStyles(doc.documentType).border}`,
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Box>
                {doc.url && (
                  <Typography variant="caption" color="textSecondary" sx={{ wordBreak: 'break-all', display: 'block' }}>
                    {doc.url.length > 60 ? `${doc.url.substring(0, 60)}...` : doc.url}
                  </Typography>
                )}
              </div>

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                {doc.url && (
                  <>
                    <IconButton
                      size="small"
                      component={Link}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: '#3b82f6' }}
                      title="View"
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      component={Link}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      sx={{ color: '#10b981' }}
                      title="Download"
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  </>
                )}
                {isCustomerDocs ? null : isSharedWithCustomer ? (
                  <Chip
                    label="Shared with Customer"
                    size="small"
                    sx={{
                      backgroundColor: '#dcfce7',
                      color: '#166534',
                      border: '1px solid #86efac',
                      fontWeight: 600
                    }}
                  />
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => handleCopyDocument(doc, 'supplier')}
                    disabled={copyLoading}
                    sx={{ color: '#8b5cf6' }}
                    title="Copy to Customer"
                  >
                    <CopyAll fontSize="small" />
                  </IconButton>
                )}
                <IconButton 
                  size="small" 
                  onClick={() => openDeleteDialog(index, isCustomerDocs)}
                  color="error"
                  disabled={deleteLoading}
                  title="Delete"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          );
        })}
      </div>
    );
  };

  const canSubmitBoardingPassResolution =
    ambiguousBoardingPassMatches.length > 0 &&
    ambiguousBoardingPassMatches.every((match) =>
      Boolean(boardingPassResolutionSelections[match.resolutionToken])
    );

  return (
    <div className="space-y-6">
      {/* ========== SOURCE SECTION ========== */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Source Information</h2>
              <p className="text-xs text-gray-500">Booking source and attachments</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isEmailSource && (
              <button
                onClick={handleFetchEmailText}
                disabled={emailLoading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25 disabled:opacity-50"
              >
                {emailLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Fetching...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Fetch Email Text
                  </>
                )}
              </button>
            )}
            {/* Expand/Collapse Toggle */}
            <button
              onClick={() => setSourceExpanded(!sourceExpanded)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-200 transition-all"
            >
              {sourceExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Expand
                </>
              )}
            </button>
          </div>
        </Box>

        {/* Source Documents - Collapsible */}
        {sourceExpanded && hasSource && (
          <>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
            <div className="px-6 py-4 bg-blue-50 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Source Documents</h3>
            </div>
            
            <div className="p-6 space-y-4">
              {source.map((item, index) => {
                const isEmail = item.name?.toLowerCase() === 'email';
                
                return (
                  <div 
                    key={index}
                    className="bg-gray-50 rounded-xl p-5 border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      {/* Source Type Badge */}
                      <div className={`flex-shrink-0 px-4 py-2 rounded-xl border ${getSourceBadgeColor(item.name)}`}>
                        <div className="flex items-center gap-2">
                          {getSourceIcon(item.name)}
                          <span className="text-sm font-semibold">{item.name || 'Unknown'}</span>
                        </div>
                      </div>
                      
                      {/* Source Details */}
                      <div className="flex-1 min-w-0">
                        {isEmail ? (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Hash className="w-3.5 h-3.5" />
                              <span className="font-medium uppercase tracking-wider">Message ID</span>
                            </div>
                            <p className="text-sm font-mono text-gray-700 bg-white rounded-lg px-3 py-2 border border-gray-200 break-all">
                              {item.sourceId || 'N/A'}
                            </p>
                            {item.createdAt && (
                              <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                                <Calendar className="w-3.5 h-3.5" />
                                <span>Added: {formatDate(item.createdAt)}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Link2 className="w-3.5 h-3.5" />
                              <span className="font-medium uppercase tracking-wider">Source ID / URL</span>
                            </div>
                            <p className="text-sm text-gray-700 break-all">
                              {item.sourceId || 'N/A'}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Actions */}
                      {item.sourceId && !isEmail && (
                        <a 
                          href={item.sourceId}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-white text-blue-600 text-xs font-medium rounded-lg border border-blue-100 hover:bg-blue-50 transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Open
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Source Attachments Section (if any) */}
          {source.some(item => item.attachments && item.attachments.length > 0) && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
              <div className="px-6 py-4 bg-blue-50 border-b border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Source Attachments</h3>
              </div>
              
              <div className="p-6 space-y-3">
                {source.map((item, itemIndex) => 
                  item.attachments?.map((attachment, attIndex) => (
                    <div 
                      key={`${itemIndex}-${attIndex}`}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-300"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">
                            {attachment.name || `Attachment ${attIndex + 1}`}
                          </p>
                          {attachment.url && (
                            <p className="text-xs text-gray-500 truncate">
                              {attachment.url.length > 50 ? `${attachment.url.substring(0, 50)}...` : attachment.url}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {attachment.url && (
                        <div className="flex items-center gap-2 ml-4">
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-colors shadow-sm"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          <a
                            href={attachment.url}
                            download
                            className="p-2 rounded-lg bg-white text-blue-600 hover:bg-blue-50 transition-colors shadow-sm"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          </>
        )}
      </Paper>
      
      {!hasSource && (
        <Paper sx={{ p: 3 }}>
          <div className="bg-white rounded-xl p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">No Source Documents</h3>
            <p className="text-xs text-gray-500">No source information available for this booking.</p>
          </div>
        </Paper>
      )}

      {/* ========== DOCUMENTS SECTION ========== */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Documents</h2>
              <p className="text-xs text-gray-500">Booking documents and attachments</p>
            </div>
          </div>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => setShowUploadDialog(true)}
          >
            Upload
          </Button>
        </Box>

        <Tabs value={activeDocumentTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab 
            label={`All Documents${hasAttachments ? ` (${attachments.length})` : ''}`} 
            value="supplier" 
          />
          <Tab 
            label={`Customer Documents${hasCustomerDocuments ? ` (${customerDocuments.length})` : ''}`} 
            value="customer" 
          />
        </Tabs>

        {/* Supplier Documents Tab */}
        {activeDocumentTab === 'supplier' && (
          <Box sx={{ mt: 2 }}>
            {renderDocumentList(attachments, false)}
          </Box>
        )}

        {/* Customer Documents Tab */}
        {activeDocumentTab === 'customer' && (
          <Box sx={{ mt: 2 }}>
            {renderDocumentList(customerDocuments, true)}
          </Box>
        )}
      </Paper>

      {/* ========== DIALOGS ========== */}

      {/* Email Text Dialog */}
      <Modal 
        isOpen={showEmailDialog} 
        onClose={() => setShowEmailDialog(false)}
        title="Email Text Content"
      >
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <textarea
              readOnly
              value={emailText}
              rows={15}
              className="w-full text-sm font-mono text-gray-700 bg-transparent resize-none focus:outline-none"
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => setShowEmailDialog(false)}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onClose={() => {
        setShowUploadDialog(false);
        setShareWithCustomer(false);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <FormControl fullWidth error={!fileType}>
              <InputLabel>Document Type *</InputLabel>
              <Select
                value={fileType}
                onChange={(e) => setFileType(e.target.value)}
                label="Document Type *"
              >
                <MenuItem value="">
                  <em>Select a type...</em>
                </MenuItem>
                {fileTypeOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              component="label"
              startIcon={<Upload />}
              fullWidth
              color={selectedFile ? "success" : "primary"}
            >
              {selectedFile 
                ? (selectedFile.name.length > 20 
                    ? `${selectedFile.name.substring(0, 20)}...` 
                    : selectedFile.name)
                : 'Select File'}
              <input
                type="file"
                hidden
                onChange={handleFileSelect}
                accept=".pdf,.jpg,.jpeg,.png"
              />
            </Button>
            {selectedFile && (
              <Typography variant="caption" color="textSecondary">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </Typography>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={shareWithCustomer}
                  onChange={(event) => setShareWithCustomer(event.target.checked)}
                />
              }
              label="Share this document with customer"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowUploadDialog(false);
            setShareWithCustomer(false);
          }} disabled={uploadLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpload} 
            variant="contained" 
            disabled={!selectedFile || !fileType || uploadLoading}
          >
            {uploadLoading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Boarding Pass Traveller Resolution Dialog */}
      <Dialog
        open={ambiguousBoardingPassMatches.length > 0}
        onClose={() => {
          if (!boardingPassResolveLoading) {
            clearBoardingPassResolutionDialog();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertTriangle className="text-amber-600" />
          Match boarding pass traveller
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Select the booking traveller for each boarding pass that could not be matched automatically.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {ambiguousBoardingPassMatches.map((match, index) => {
              const passengerName =
                match.passenger?.fullName ||
                formatTravellerName(
                  match.passenger?.firstName,
                  match.passenger?.lastName,
                  'UNKNOWN PASSENGER'
                );
              const routeLabel =
                match.flight?.departureIata || match.flight?.arrivalIata
                  ? `${match.flight?.departureIata || '-'} - ${match.flight?.arrivalIata || '-'}`
                  : null;
              const selectId = `boarding-pass-resolution-${index}`;

              return (
                <Box
                  key={match.resolutionToken}
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 2,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" variant="outlined" label={`Boarding pass ${index + 1}`} />
                      {match.flight?.flightNumber && (
                        <Chip size="small" label={match.flight.flightNumber} />
                      )}
                      {match.passenger?.seatNumber && (
                        <Chip size="small" label={`Seat ${match.passenger.seatNumber}`} />
                      )}
                    </Box>
                    <Typography variant="subtitle2">{passengerName}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {routeLabel && (
                        <Typography variant="caption" color="textSecondary">
                          {routeLabel}
                        </Typography>
                      )}
                      {match.passenger?.pnr && (
                        <Typography variant="caption" color="textSecondary">
                          PNR {match.passenger.pnr}
                        </Typography>
                      )}
                      {match.passenger?.sequenceNumber && (
                        <Typography variant="caption" color="textSecondary">
                          Seq {match.passenger.sequenceNumber}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <FormControl fullWidth>
                    <InputLabel id={`${selectId}-label`}>Booking traveller</InputLabel>
                    <Select
                      labelId={`${selectId}-label`}
                      id={selectId}
                      value={boardingPassResolutionSelections[match.resolutionToken] || ''}
                      label="Booking traveller"
                      displayEmpty
                      onChange={(event) => {
                        setBoardingPassResolutionSelections((current) => ({
                          ...current,
                          [match.resolutionToken]: event.target.value
                        }));
                      }}
                      disabled={boardingPassResolveLoading}
                    >
                      <MenuItem value="">
                        <em>Select traveller...</em>
                      </MenuItem>
                      {match.candidates.map((candidate) => (
                        <MenuItem
                          key={candidate.travellerId}
                          value={String(candidate.travellerId)}
                        >
                          {formatTravellerName(candidate.firstName, candidate.lastName)}
                          {candidate.seat ? ` - Seat ${candidate.seat}` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={clearBoardingPassResolutionDialog}
            disabled={boardingPassResolveLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResolveBoardingPassMatches}
            variant="contained"
            disabled={!canSubmitBoardingPassResolution || boardingPassResolveLoading}
          >
            {boardingPassResolveLoading ? 'Assigning...' : 'Assign Boarding Pass'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={showDeleteDialog} 
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertTriangle className="text-red-600" />
          Delete Document
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1">
            Are you sure you want to delete this document?
          </Typography>
          {documentToDelete?.doc?.name && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              <strong>{documentToDelete.doc.name}</strong>
            </Typography>
          )}
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={closeDeleteDialog} 
            disabled={deleteLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained"
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? null : <Delete />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default B4FSourceDocumentsTab;
